import { useCallback, useEffect, useState } from 'react'
import { CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDto } from 'api/Clients/Types/Client'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { FilterType } from 'components/Filter'
import { getClientFilterFields } from 'components/Filter/ClientFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientStatusAction, fetchClientTypesAction } from 'store/clients/clients.actions'
import { fetchAdministrationUsersAction } from 'store/identity/identity.actions'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import './RecordsClients.scss'

import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useApiClient } from '@/utils/api/ApiClient'
import { ClientsApiClient } from '@/utils/api/clientsApi'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'

const useMapStateToProps = () => ({
  clientTypes: useAppSelector((state) => state.clients.types),
  clientStatus: useAppSelector((state) => state.clients.status),
  types: useAppSelector((state) => state.clients.types),
  users: useAppSelector((state) => state.identity.administrationUsers),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  return {
    fetchAdministrationUsers: () => dispatch(fetchAdministrationUsersAction(identityApiClient)),
    fetchClientTypes: () => dispatch(fetchClientTypesAction(clientsApiClient)),
    fetchClientStatus: () => dispatch(fetchClientStatusAction(clientsApiClient)),
  }
}

export type SelectClientEvent = { filterId?: string; value: ClientDto[] }

interface SelectClientsProps {
  filterId?: string
  onSelect: (props: SelectClientEvent) => void
  onClose: () => void
}

export const SelectClientTable = (props: SelectClientsProps): JSX.Element => {
  const clientsApiClient = useApiClient(ClientsApiClient)

  const { gridState } = useGridState()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [clients, setClients] = useState<ClientDto[]>([])

  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedClients, setSelectedClients] = useState<ClientDto[]>([])

  const { users, clientTypes, clientStatus } = useMapStateToProps()
  const { fetchAdministrationUsers, fetchClientStatus, fetchClientTypes } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterActionState: GridFilterActionState): Promise<PageData<ClientDto[]>> => {
          const response = await clientsApiClient.getClients({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          if (isEmpty(clientTypes)) await fetchClientTypes()
          if (isEmpty(clientStatus)) await fetchClientStatus()
          if (isEmpty(users)) await fetchAdministrationUsers()

          const clientFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value = clientFilter?.value?.filter((client: ClientDto) => client) || []

          setSelectedClients(value)
          if (response?.items) {
            setClients(
              response.items.map((client: ClientDto) => {
                if (value.length <= 0) {
                  return { ...client, selected: false }
                }

                const match = value?.find((x: ClientDto) => x.id === client.id)
                const isSelected = !!match

                return { ...client, selected: isSelected }
              })
            )
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<ClientDto[]>>
  ) => {
    setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientsApiClient.getClients({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setClients(response.items.map((x) => ({ ...x, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [getQuery, gridFilterState, gridState.pageSize, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const selectClients = (clientItems: ClientDto[]) =>
    clientItems.map((client: ClientDto) => {
      const match = selectedClients.find((x) => x.id === client.id)
      const isSelected = !!match
      return { ...client, selected: isSelected }
    })

  const handleSubmitClient = () => {
    props.onSelect({
      filterId: props.filterId,
      value: uniqBy(selectedClients, 'id'),
    })
    props.onClose()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await clientsApiClient.getClients({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setClients(selectClients(response.items))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setClients(clients.map((client) => ({ ...client, selected: isChecked })))

    const clientsSelected = isChecked
      ? uniqBy([...clients, ...selectedClients], 'id')
      : selectedClients.filter((selectedClient) => {
          const hasClient = clients.find((x) => x.id === selectedClient.id)

          return !hasClient
        })

    setSelectedClients(clientsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const client = clients.find((x) => x.id === event.dataItem.id)
      if (client) {
        setSelectedClients([...selectedClients, client])
      }
    } else {
      setSelectedClients(selectedClients.filter((x) => x.id !== event.dataItem.id))
    }

    setClients(
      clients.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <DialogContent size='3xl'>
      <DialogHeader>
        <DialogTitle>Select Client</DialogTitle>
        <SelectFiltersFacade
          gridState={gridFilterState}
          setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
          filterFetch={filterFetch}
          filterType={FilterType.CLIENT}
          filterFields={getClientFilterFields()}
          containerClassName='records-clients__filter'
          getQuery={getQuery}
        />
      </DialogHeader>
      <Grid
        {...gridFilterState}
        resizable
        data={clients}
        selectedField='selected'
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ maxHeight: '70vh' }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={clients.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        <Column field='name' title='Name' />
        <Column field='type.name' title='Client type' />
        <Column field='status.name' title='Status' />
        <Column field='country.name' title='Country' />
        <Column field='emailAddress' title='Email' />
        <Column field='phone' title='Phone' />
        <Column
          width={80}
          minResizableWidth={80}
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem?.tickmillCompany?.id) {
              return <td />
            }

            return (
              <td>
                <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                  {gridCellProps.dataItem.tickmillCompany.name}
                </CompanyTag>
              </td>
            )
          }}
          title='Entity'
        />
      </Grid>
      <DialogFooter>
        <Button onClick={handleSubmitClient}>Save</Button>
      </DialogFooter>
    </DialogContent>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}
